import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  BuildingOfficeIcon,
  MapPinIcon,
  PlusIcon,
  PencilIcon,
  StarIcon
} from '@heroicons/react/24/outline';
import api from '../../../services/api';
import LoadingSpinner from '../../../components/ui/LoadingSpinner';
import { useDebounce } from '../../../hooks/useDebounce';

const MIN_SEARCH_LENGTH = 3;

interface Place {
  id: string;
  name: string;
  description: string;
  city: string | null;
  state: string | null;
  address: string;
  status: string;
  businessUserInfo?: {
    id: string;
    name: string | null;
    role: string;
  };
  logoUrl: string;
  coverImageUrl: string;
  instagram: string;
  redeemCode: string | null;
  isRedeemCodeEnabled: boolean;
  isPartner: boolean;
  partnerTermsAcceptedAt: string | null;
}

interface PlaceStats {
  totalPlaces: number;
  activePlaces: number;
  inactivePlaces: number;
  pendingPlaces: number;
  partnerPlaces: number;
  nonPartnerPlaces: number;
}

interface PlacesResponse {
  places: Place[];
  pagination: {
    page: number;
    pageSize: number;
    totalCount: number;
    totalPages: number;
  };
}

const PlacesPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [partnerFilter, setPartnerFilter] = useState<string>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(20);

  const debouncedSearchTerm = useDebounce(searchTerm, 400);
  const effectiveSearch = debouncedSearchTerm.length >= MIN_SEARCH_LENGTH ? debouncedSearchTerm : '';

  // Buscar cafeterias
  const { data: placesData, isLoading: isLoadingPlaces } = useQuery({
    queryKey: ['places', effectiveSearch, statusFilter, partnerFilter, currentPage, pageSize],
    queryFn: async () => {
      const response = await api.get('/admin/places', {
        params: {
          page: currentPage,
          pageSize,
          search: effectiveSearch,
          status: statusFilter !== 'all' ? statusFilter : undefined,
          isPartner: partnerFilter !== 'all' ? partnerFilter === 'partner' : undefined
        }
      });
      return response.data as PlacesResponse;
    }
  });

  const places = placesData?.places || [];
  const pagination = placesData?.pagination;

  // Buscar estatísticas
  const { data: stats, isLoading: isLoadingStats } = useQuery({
    queryKey: ['places-stats'],
    queryFn: async () => {
      const response = await api.get('/admin/places/stats');
      return response.data as PlaceStats;
    }
  });

  // Alternar status de parceria
  const togglePartnerMutation = useMutation({
    mutationFn: async ({ placeId, isPartner }: { placeId: string; isPartner: boolean }) => {
      const response = await api.patch(`/admin/places/${placeId}/partner-status`, { isPartner });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['places'] });
      queryClient.invalidateQueries({ queryKey: ['places-stats'] });
    }
  });

  const handleTogglePartner = (place: Place) => {
    if (place.isPartner) {
      const confirmed = window.confirm(
        `Revogar a parceria de "${place.name}"? Ela deixará de aparecer como parceira no app.`
      );
      if (!confirmed) return;
    }
    togglePartnerMutation.mutate({ placeId: place.id, isPartner: !place.isPartner });
  };





  // Função para mudar de página
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Função para aplicar filtros
  const handleApplyFilters = () => {
    setCurrentPage(1); // Reset para primeira página ao aplicar filtros
  };

  if (isLoadingPlaces || isLoadingStats) {
    return <LoadingSpinner />;
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Gerenciar Cafeterias</h1>
        <p className="text-gray-600">Gerencie todas as cafeterias do sistema</p>
      </div>

      {/* Estatísticas */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <BuildingOfficeIcon className="h-8 w-8 text-blue-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total de Cafeterias</p>
              <p className="text-2xl font-bold text-gray-900">{stats?.totalPlaces || 0}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="h-8 w-8 bg-green-100 rounded-full flex items-center justify-center">
              <span className="text-green-600 text-sm font-bold">✓</span>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Ativas</p>
              <p className="text-2xl font-bold text-gray-900">{stats?.activePlaces || 0}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="h-8 w-8 bg-red-100 rounded-full flex items-center justify-center">
              <span className="text-red-600 text-sm font-bold">✗</span>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Inativas</p>
              <p className="text-2xl font-bold text-gray-900">{stats?.inactivePlaces || 0}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="h-8 w-8 bg-yellow-100 rounded-full flex items-center justify-center">
              <span className="text-yellow-600 text-sm font-bold">⏳</span>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Pendentes</p>
              <p className="text-2xl font-bold text-gray-900">{stats?.pendingPlaces || 0}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="h-8 w-8 bg-emerald-100 rounded-full flex items-center justify-center">
              <StarIcon className="h-4 w-4 text-emerald-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Parceiras</p>
              <p className="text-2xl font-bold text-gray-900">{stats?.partnerPlaces || 0}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="h-8 w-8 bg-gray-100 rounded-full flex items-center justify-center">
              <span className="text-gray-500 text-sm font-bold">–</span>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Não parceiras</p>
              <p className="text-2xl font-bold text-gray-900">{stats?.nonPartnerPlaces || 0}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filtros e Busca */}
      <div className="bg-white rounded-lg shadow mb-6">
        <div className="p-6 border-b border-gray-200">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <input
                type="text"
                placeholder="Buscar por nome ou cidade..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              />
            </div>
            <div className="sm:w-48">
                             <select
                 value={statusFilter}
                 onChange={(e) => setStatusFilter(e.target.value)}
                 className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
               >
                 <option value="all">Todos os Status</option>
                 <option value="Active">Ativas</option>
                 <option value="Inactive">Inativas</option>
                 <option value="Pending">Pendentes</option>
                 <option value="Suspended">Suspensas</option>
               </select>
            </div>
            <div className="sm:w-48">
              <select
                value={partnerFilter}
                onChange={(e) => setPartnerFilter(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              >
                <option value="all">Todas (parceria)</option>
                <option value="partner">Parceiras</option>
                <option value="non-partner">Não parceiras</option>
              </select>
            </div>
            <button
              onClick={handleApplyFilters}
              className="btn btn-primary"
            >
              Aplicar Filtros
            </button>
                         <button 
               onClick={() => navigate('/places/new')}
               className="px-6 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors flex items-center"
             >
               <PlusIcon className="h-5 w-5 mr-2" />
               Nova Cafeteria
             </button>
          </div>
        </div>
      </div>

      {/* Tabela de Cafeterias */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Cafeteria
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Localização
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Proprietário
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Parceria
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {places.map((place) => (
                <tr key={place.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      {place.logoUrl ? (
                        <img 
                          src={place.logoUrl} 
                          alt="Logo da cafeteria" 
                          className="h-8 w-8 rounded-lg object-cover mr-3"
                        />
                      ) : (
                        <BuildingOfficeIcon className="h-8 w-8 text-gray-400 mr-3" />
                      )}
                      <div>
                        <div className="text-sm font-medium text-gray-900">{place.name}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <MapPinIcon className="h-4 w-4 text-gray-400 mr-2" />
                      <span className="text-sm text-gray-900">
                        {place.city && place.state ? `${place.city}, ${place.state}` : 'Localização não definida'}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {place.businessUserInfo?.name || 'Não definido'}
                      {place.businessUserInfo?.role && (
                        <div className="text-xs text-gray-500">({place.businessUserInfo.role})</div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      place.status === 'Active' 
                        ? 'bg-green-100 text-green-800' 
                        : place.status === 'Pending'
                        ? 'bg-yellow-100 text-yellow-800'
                        : place.status === 'Suspended'
                        ? 'bg-orange-100 text-orange-800'
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {place.status === 'Active' ? 'Ativa' :
                       place.status === 'Pending' ? 'Pendente' :
                       place.status === 'Suspended' ? 'Suspensa' : 'Inativa'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      place.isPartner
                        ? 'bg-emerald-100 text-emerald-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {place.isPartner ? 'Parceira' : 'Não parceira'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => handleTogglePartner(place)}
                        disabled={togglePartnerMutation.isPending}
                        className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 ${
                          place.isPartner
                            ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            : 'bg-emerald-600 text-white hover:bg-emerald-700'
                        }`}
                        title={place.isPartner ? 'Revogar parceria' : 'Promover a parceira'}
                      >
                        <StarIcon className="h-4 w-4" />
                        {place.isPartner ? 'Revogar' : 'Promover'}
                      </button>
                      <button
                        onClick={() => navigate(`/places/${place.id}/edit`)}
                        className="btn btn-primary flex items-center gap-2"
                        title="Editar cafeteria"
                      >
                        <PencilIcon className="h-4 w-4" />
                        Editar
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
                 </div>
       </div>

       {/* Paginação */}
       {pagination && pagination.totalPages > 1 && (
         <div className="bg-white rounded-lg shadow mt-6">
           <div className="px-6 py-3 border-t border-gray-200">
             <div className="flex items-center justify-between">
               <div className="text-sm text-gray-700">
                 Mostrando <span className="font-medium">{((pagination.page - 1) * pagination.pageSize) + 1}</span> a{' '}
                 <span className="font-medium">
                   {Math.min(pagination.page * pagination.pageSize, pagination.totalCount)}
                 </span> de{' '}
                 <span className="font-medium">{pagination.totalCount}</span> resultados
               </div>
               <div className="flex items-center space-x-2">
                 <button
                   onClick={() => handlePageChange(pagination.page - 1)}
                   disabled={pagination.page === 1}
                   className="px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                 >
                   Anterior
                 </button>
                 
                 {/* Páginas numeradas */}
                 <div className="flex items-center space-x-1">
                   {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                     let pageNum;
                     if (pagination.totalPages <= 5) {
                       pageNum = i + 1;
                     } else if (pagination.page <= 3) {
                       pageNum = i + 1;
                     } else if (pagination.page >= pagination.totalPages - 2) {
                       pageNum = pagination.totalPages - 4 + i;
                     } else {
                       pageNum = pagination.page - 2 + i;
                     }
                     
                     return (
                       <button
                         key={pageNum}
                         onClick={() => handlePageChange(pageNum)}
                         className={`px-3 py-1 rounded-md text-sm font-medium ${
                           pageNum === pagination.page
                             ? 'bg-emerald-600 text-white'
                             : 'border border-gray-300 text-gray-700 hover:bg-gray-50'
                         }`}
                       >
                         {pageNum}
                       </button>
                     );
                   })}
                 </div>
                 
                 <button
                   onClick={() => handlePageChange(pagination.page + 1)}
                   disabled={pagination.page === pagination.totalPages}
                   className="px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                 >
                   Próxima
                 </button>
               </div>
             </div>
           </div>
         </div>
       )}

       
    </div>
  );
};

export default PlacesPage; 